import { useWatch } from "react-hook-form";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BaseForm, FormTextarea } from "components/BaseForm";
import EmailHtmlFrame from "components/email/EmailHtmlFrame";
import { useDebounce } from "hooks/useDebounce";
import EmailTemplateService from "services/EmailTemplate";
import { decodeTemplateHtml } from "routes/email/emailTemplateCache";

const PREVIEW_DEBOUNCE_MS = 400;
const PREVIEW_MIN_HEIGHT = 400;

const schema = z.object({
  html: z.string().trim().min(1, "HTML is required"),
});

// Debounced because every srcDoc change reloads the iframe. autoHeight fits
// the frame to the email body, so the minimum keeps a short template from
// collapsing into a thin strip.
const HtmlPreview = () => {
  const html = useWatch({ name: "html" });
  const debouncedHtml = useDebounce(html, PREVIEW_DEBOUNCE_MS);
  return (
    <EmailHtmlFrame
      title="HTML preview"
      html={debouncedHtml ?? ""}
      autoHeight
      style={{ minHeight: PREVIEW_MIN_HEIGHT }}
    />
  );
};

/** Hand-edited template markup with a live preview. */
const HtmlEmailTemplate = ({ template, onSaved, onCancel }) => {
  const save = continueToNext => async values => {
    const saved = await EmailTemplateService.editEmailTemplate({
      id: template.id,
      html: values.html,
    });
    await onSaved(saved, { continueToNext });
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={{ html: decodeTemplateHtml(template) }}
      onSubmit={save(true)}
      onCancel={onCancel}
      submitButtonText="Save & Continue"
      secondButton={{ content: "Save", onSubmit: save(false) }}
    >
      {/* Stacked rather than side by side: emails are laid out for a width a
          half-page column cannot always give, and long markup lines wrap less
          at full width. */}
      <Card>
        <CardHeader>
          <CardTitle>HTML</CardTitle>
        </CardHeader>
        <CardContent>
          {/* A minimum height rather than `rows`: the shadcn Textarea sizes
              itself to its content and ignores `rows`. */}
          <FormTextarea
            name="html"
            aria-label="HTML"
            className="tw:min-h-[40vh] tw:max-h-[75vh] tw:font-mono tw:text-xs"
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <HtmlPreview />
        </CardContent>
      </Card>
    </BaseForm>
  );
};

export default HtmlEmailTemplate;
